import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/services/api'
import { Bell, Save, Trash2, HardDrive, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import clsx from 'clsx'

export default function SettingsPage() {
  const qc = useQueryClient()
  const [testChatId, setTestChatId] = useState('')
  const [cleanupDays, setCleanupDays] = useState(30)
  const [cleaning, setCleaning] = useState(false)

  const { data: notifSettings } = useQuery({
    queryKey: ['notif-settings'],
    queryFn: () => api.get('/notifications/settings/').then((r) => r.data.results || r.data),
  })

  const { data: storageStats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['storage-stats'],
    queryFn: () => api.get('/attendance/attendance/storage-stats/').then((r) => r.data),
  })

  const handleTestTelegram = async () => {
    try {
      await api.post('/notifications/settings/test/', { chat_id: testChatId })
      toast.success('Тестовое сообщение отправлено!')
    } catch {
      toast.error('Ошибка отправки')
    }
  }

  const handleCleanup = async () => {
    if (!window.confirm(`Удалить все фото проходов старше ${cleanupDays} дней?`)) return
    setCleaning(true)
    try {
      const resp = await api.post('/attendance/attendance/cleanup-photos/', { days: cleanupDays })
      toast.success(resp.data.message)
      setTimeout(() => refetchStats(), 3000)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка очистки')
    } finally {
      setCleaning(false)
    }
  }

  const DAY_OPTIONS = [7, 14, 30, 60, 90]

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900">Настройки системы</h1>

      {/* Storage / Photo Cleanup */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <HardDrive size={18} className="text-rose-500" />
          <h2 className="font-semibold text-gray-800">Хранилище фото</h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Файлов на диске', value: statsLoading ? '—' : (storageStats?.files_on_disk ?? '—') },
            { label: 'Записей в БД', value: statsLoading ? '—' : (storageStats?.photo_records ?? '—') },
            { label: 'Занято', value: statsLoading ? '—' : `${storageStats?.total_size_mb ?? '—'} MB` },
          ].map((s) => (
            <div key={s.label} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
              <p className="text-lg font-black text-slate-900">{s.value}</p>
              <p className="text-[10px] font-medium text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => refetchStats()}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors mb-5"
        >
          <RefreshCw size={12} /> Обновить статистику
        </button>

        {/* Cleanup controls */}
        <div className="space-y-3">
          <p className="text-sm text-gray-500">Удалить фото старше:</p>
          <div className="flex gap-2 flex-wrap">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setCleanupDays(d)}
                className={clsx(
                  'px-4 py-2 rounded-xl text-sm font-bold border transition-all',
                  cleanupDays === d
                    ? 'bg-rose-600 text-white border-rose-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-rose-300 hover:text-rose-600'
                )}
              >
                {d} дн.
              </button>
            ))}
          </div>
          <button
            onClick={handleCleanup}
            disabled={cleaning}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-bold hover:bg-rose-600 hover:text-white hover:border-rose-600 disabled:opacity-50 transition-all"
          >
            {cleaning
              ? <><RefreshCw size={14} className="animate-spin" /> Очистка...</>
              : <><Trash2 size={14} /> Очистить фото старше {cleanupDays} дней</>
            }
          </button>
          <p className="text-xs text-slate-400">
            Фото удаляются с диска, записи о проходах остаются.
            Новые фото сжимаются до 50 KB автоматически.
          </p>
        </div>
      </div>

      {/* Telegram */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Bell size={18} className="text-primary-600" />
          <h2 className="font-semibold text-gray-800">Telegram уведомления</h2>
        </div>

        <div className="space-y-4">
          {(notifSettings || []).map((s) => (
            <div key={s.id} className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">{s.trigger}</p>
                <p className="text-xs text-gray-400">Chat IDs: {(s.chat_ids || []).join(', ') || 'не указаны'}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked={s.is_enabled} />
                <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600" />
              </label>
            </div>
          ))}

          <div className="pt-2">
            <label className="label">Тест Telegram</label>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="Chat ID (например: -100123456789)"
                value={testChatId}
                onChange={(e) => setTestChatId(e.target.value)}
              />
              <button className="btn-primary" onClick={handleTestTelegram}>
                Отправить тест
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* System info */}
      <div className="card">
        <h2 className="font-semibold text-gray-800 mb-4">Информация о системе</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Версия</span><span className="font-medium">1.0.0</span></div>
          <div className="flex justify-between"><span className="text-gray-500">API</span><span className="font-mono text-xs">/api/v1/</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Webhook</span><span className="font-mono text-xs">/webhook/hikvision/</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Документация</span><a href="/api/docs/" target="_blank" className="text-primary-600 hover:underline">Swagger UI</a></div>
        </div>
      </div>
    </div>
  )
}
